import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { debounce } from 'lodash';
import {
  withStyles,
  CircularProgress,
  Typography,
  Grid,
  Checkbox,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
} from '@material-ui/core';
import ArrowBack from '@material-ui/icons/KeyboardArrowLeft';
import ArrowForward from '@material-ui/icons/KeyboardArrowRight';
import { getEventTemplates } from '@pingum/app-state-management/eventTemplate/actions';
import StatusBadge from '../../../widgets/StatusBadge';
import Tag from '../../../widgets/Tag';
import DisplayMessage from '../../../widgets/DisplayMessage';
import styles from './styles';

export const DEBOUNCE_TIME = 500;

export const isLoadingMainContent
 = (isFetchingEventTemplates, isFetchingEventSubscription) => isFetchingEventTemplates || isFetchingEventSubscription;

export const getPaginationInfo = (eventTemplates) => {
  const startingNumber = eventTemplates.number * eventTemplates.size;
  const endingNumber = startingNumber + eventTemplates.numberOfElements;
  return `showing ${startingNumber}-${endingNumber} of ${eventTemplates.totalElements}`;
};

export class EventTemplateSubscriptionsCardComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      pagination: {
        page: 0,
        size: 10,
        search: '',
        sort: ['name,asc'],
      },
      searchText: '',
    };

    this.props.getEventTemplates(false, this.state.pagination);
  }

  componentDidMount() {
    this.debouncedSearchEventTemplates = debounce(this.searchEventTemplates, DEBOUNCE_TIME);
  }

  handleChangeSubscription = (event) => {
    const eventTemplateId = event.target.value;
    if (this.props.eventSubscription.eventTemplates.indexOf(parseInt(eventTemplateId, 10)) >= 0) {
      // remove subscription
      this.props.updateEventSubscription(this.props.profile.user_id, { eventTemplates: { remove: [eventTemplateId] } });
    }

    // add subscription
    this.props.updateEventSubscription(this.props.profile.user_id, { eventTemplates: { add: [eventTemplateId] } });
  }

  handleSearchChange = (event) => {
    this.setState({ searchText: event.target.value }, () => {
      this.debouncedSearchEventTemplates();
    });
  }

  searchEventTemplates = () => {
    this.setState((prevState) => ({
      pagination: {
        ...prevState.pagination,
        search: prevState.searchText,
        page: 0,
      },
    }), () => {
      this.props.getEventTemplates(false, this.state.pagination);
    });
  }

  handlePageNext = () => {
    this.setState((prevState) => ({
      pagination: {
        ...prevState.pagination,
        page: prevState.pagination.page + 1,
      },
    }), () => {
      this.props.getEventTemplates(false, this.state.pagination);
    });
  }

  handlePagePrevious = () => {
    this.setState((prevState) => ({
      pagination: {
        ...prevState.pagination,
        page: prevState.pagination.page - 1,
      },
    }), () => {
      this.props.getEventTemplates(false, this.state.pagination);
    });
  }

  render() {
    const {
      classes,
      isFetchingEventTemplates,
      eventTemplates,
      isFetchingEventSubscription,
      isEditingEventSubscription,
      eventSubscription,
    } = this.props;
    return (
      <div>
        <div className={classes.header}>
          <Typography variant="h3" className={classes.categoryTitle} id="event-templates-group-title" gutterBottom>
            Workflows
          </Typography>
          <TextField
            id="search-event-templates"
            label="Search"
            className={classes.searchField}
            value={this.state.searchText}
            onChange={this.handleSearchChange}
            margin="normal"
            variant="outlined"
          />
        </div>
        <div className={classNames(classes.layout, classes.cardGrid)}>
          {
            isLoadingMainContent(isFetchingEventTemplates, isFetchingEventSubscription)
              ? <div style={{ textAlign: 'center' }}><CircularProgress size={80} /></div>
              : (
                <Grid container spacing={2}>
                  {
                    eventTemplates.content.length === 0
                      ? (
                        <Grid item>
                          <DisplayMessage
                            id="no-event-templates-text"
                            title="No Workflows"
                            subTitle="Your organization does not have any defined workflows - only admins can create these"
                            icon="info_outline"
                          />
                        </Grid>
                      )
                      : eventTemplates.content.map((eventTemplate) => (
                        <Grid item key={eventTemplate.id} xs={12} sm={6} md={4} lg={3}>
                          <Card className={classes.card}>
                            <CardHeader
                              title={<Typography variant="subtitle1">{eventTemplate.name}</Typography>}
                              subheader={eventTemplate.description}
                              avatar={(
                                <StatusBadge
                                  icon={eventTemplate.defaultStartStatus.icon}
                                  iconColor={
                                      eventTemplate.defaultStartStatus.iconColor
                                    }
                                />
                              )}
                              action={(
                                <Checkbox
                                  checked={
                                    eventSubscription.eventTemplates.indexOf(eventTemplate.id) >= 0
                                  }
                                  onChange={this.handleChangeSubscription}
                                  value={eventTemplate.id}
                                  disabled={isEditingEventSubscription}
                                />
                              )}
                              classes={{ action: classes.action }}
                            />
                            <CardContent className={classes.cardContent}>
                              {eventTemplate.tags.map((tag) => (
                                <Tag
                                  key={`tag-${tag.id}-${eventTemplate.id}`}
                                  tag={tag}
                                  icon={tag.tagType.icon}
                                  color={tag.tagType.iconColor}
                                />
                              ))}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))
                  }
                </Grid>
              )
          }
        </div>
        { !isLoadingMainContent(isFetchingEventTemplates, isFetchingEventSubscription)
          && (
            <div className={classes.paginationContainer}>
              <Button className={classes.paginationButton} size="small" disabled={eventTemplates.number === 0} onClick={this.handlePagePrevious}>
                <ArrowBack />
                Previous
              </Button>
              <Button className={classes.paginationButton} size="small" onClick={this.handlePageNext} disabled={eventTemplates.totalPages === (eventTemplates.number + 1)}>
                Next
                <ArrowForward />
              </Button>
              <div className={classes.alignCenter}>{getPaginationInfo(eventTemplates)}</div>
            </div>
          )}
      </div>
    );
  }
}

EventTemplateSubscriptionsCardComponent.propTypes = {
  profile: PropTypes.object,
  classes: PropTypes.object,
  eventTemplates: PropTypes.object,
  isFetchingEventTemplates: PropTypes.bool,
  getEventTemplates: PropTypes.func,
  isFetchingEventSubscription: PropTypes.bool,
  isEditingEventSubscription: PropTypes.bool,
  eventSubscription: PropTypes.object,
  updateEventSubscription: PropTypes.func,
};

EventTemplateSubscriptionsCardComponent.defaultProps = {
  profile: { user_id: '' },
  classes: {},
  eventTemplates: {
    content: [],
    numberOfElements: 0,
    totalElements: 0,
    size: 0,
  },
  getEventTemplates: () => {},
  isFetchingEventTemplates: true,
  updateEventSubscription: () => {},
  isFetchingEventSubscription: true,
  isEditingEventSubscription: false,
  eventSubscription: {
    dashboards: [],
    eventTemplates: [],
  },
};

export const mapStateToProps = (state) => ({
  profile: state.user.user,
  eventTemplates: state.eventTemplate.eventTemplatesPageable,
  isFetchingEventTemplates: state.eventTemplate.isFetchingEventTemplates,
  isFetchingEventSubscription: state.eventSubscription.isFetchingEventSubscription,
  isEditingEventSubscription: state.eventSubscription.isEditingEventSubscription,
  eventSubscription: state.eventSubscription.eventSubscription,
});

export const mapDispatchToProps
  = (dispatch) => ({
    getEventTemplates:
    (includeArchived, pagination) => dispatch(getEventTemplates(includeArchived, pagination)),
  });

export default withStyles(styles)(connect(
  mapStateToProps,
  mapDispatchToProps,
)(EventTemplateSubscriptionsCardComponent));
